import { store } from "../../../../../redux/store";
import { t } from "../../../../core/translate";
import { selectors as brandSelectors } from "../../../../../redux/brand";
import { gaEvent } from "../../../../utils/analytics";
import MenuItem from "../../../../components/list-item";
import { ltiLaunch } from "../../../../utils/location";
import { shouldShowReadOffline } from "../../../../utils/read-offline";
import { deviceCheck } from "../../../../utils/browser";
import { currentUser, navigateTo, navigateSafelyTo } from "./shared";

const _shared = (key, defaults = {}) => {
  const state = store.getState();
  const icon = brandSelectors.getBrandValue(
    state,
    `${key}.icon`,
    defaults.icon || "default"
  );
  const iconLetter = brandSelectors.getBrandValue(
    state,
    `${key}.iconLetter`,
    defaults.iconLetter
  );
  const customIcon = brandSelectors.getBrandValue(
    state,
    `${key}.customIcon`,
    defaults.customIcon
  );

  const condition =
    !!brandSelectors.getBrandValue(state, `${key}.enabled`) ||
    !!brandSelectors.getBrandValue(state, `${key}.visible`);

  const text =
    brandSelectors.getBrandValue(state, `${key}.label`) || defaults.text;

  return { icon, iconLetter, customIcon, text, condition };
};

export const getUserToken = isbn => {
  const state = store.getState();
  const user = currentUser();
  if (!Object.keys(user.contextTokens).length) {
    return null;
  } else if (isbn && user.contextTokens[isbn]) {
    return user.contextTokens[isbn];
  }
  const firstToken = Object.keys(user.contextTokens)[0];
  return user.contextTokens[firstToken];
};

export const bookshelf = () => {
  const state = store.getState();
  const showLibrary = brandSelectors.getBrandValue(state, "Library.visible");
  const bookshelfUrl = brandSelectors.getBrandValue(
    state,
    "Bookshelf.url",
    null
  );
  const bookshelfNewTab = brandSelectors.getBrandValue(
    state,
    "Bookshelf.newTab",
    false
  );
  const brandLabel = brandSelectors.getBrandValue(
    state,
    "brand",
    t("global-bar.bookshelf-online", "Bookshelf Online")
  );

  const onClick = event => {
    event.preventDefault();
    if (bookshelfUrl) {
      // If brand defines URL for bookshelf click, go there and then exit function
      const target = bookshelfNewTab ? "_blank" : false;
      navigateSafelyTo(event, bookshelfUrl, "header/bookshelf", target);
    } else if (Fraction.Router.getRoute().join().length === 0) {
      // If no brand url is defined, reload route
      gaEvent("header/bookshelf");
      window.location.reload();
    } else {
      gaEvent("header/bookshelf");
      Fraction.Router.setRoute("/");
    }
  };

  const { condition, ...other } = _shared("Bookshelf", { text: brandLabel });
  const args = Object.assign(
    {},
    { condition: condition && showLibrary, onClick },
    other
  );

  return MenuItem(args);
};

export const scoreCenter = isbn => {
  const state = store.getState();

  const baseUrl = brandSelectors.getBrandValue(
    state,
    "ScoreCenter.url",
    "https://scorecenter-dev.gc.vitalbook.com/"
  );

  const onClick = event => {
    event.preventDefault();
    const token = "?context_token=" + getUserToken(isbn);
    const urlWithIsbn = baseUrl.replace(/\/$/, "") + `/books/${isbn}` + token;
    const url = isbn ? urlWithIsbn : baseUrl + token;
    navigateTo(event, url, "header/score_center", event);
  };

  const args = Object.assign(
    {},
    { onClick, fill: "#4F7E87" },
    _shared("ScoreCenter", {
      text: t("global-bar.score_center", "Score Center")
    })
  );
  return MenuItem(args);
};

export const vstStore = () => {
  const state = store.getState();
  const storeSso = brandSelectors.getBrandValue(state, "Store.sso");
  const storeUrl = brandSelectors.getBrandedStoreUrl(state, {
    utm_medium: "bookshelf-app-header"
  });
  const storeLabel = brandSelectors.getBrandValue(
    state,
    "Store.label",
    t("global-bar.store", "Store")
  );

  const onClick = event => {
    event.preventDefault();
    const navigate = storeSso ? navigateTo : navigateSafelyTo;
    navigate(event, storeUrl, "header/store", event);
  };

  const args = Object.assign(
    {},
    { onClick, fill: "#047EA9" },
    _shared("Store", { text: storeLabel })
  );

  return MenuItem(args);
};

export const vstAnalytics = () => {
  const state = store.getState();
  const user = currentUser();
  const vstAnalyticsLabel = brandSelectors.getBrandValue(
    state,
    "VstAnalytics.label",
    t("global-bar.vst-analytics", "VST Analytics")
  );

  const instructor =
    user && user.redirectData && user.redirectData.role === "instructor";

  const onClick = event => {
    event.preventDefault();
    Fraction.Reporter.noticeInteraction("vst-analytics-apps-menu");
    if (user.contextTokens) {
      ltiLaunch(user, "vst_analytics", true, null, {
        label: vstAnalyticsLabel
      });
    }
  };

  const { condition, ...other } = _shared("VstAnalytics", {
    text: vstAnalyticsLabel
  });

  const args = Object.assign(
    {},
    { condition: condition && instructor, onClick },
    other
  );

  return MenuItem(args);
};

// FIXME It should redirect the user to the download app page if app isn't installed.
export const readOffline = () => {
  const user = currentUser();

  const onClick = event => {
    event.preventDefault();
    Fraction.Helpers.launchInApp(null, true);
  };

  const { condition, text, ...other } = _shared("Bookshelf");

  const args = Object.assign(
    {},
    {
      condition: !!(user && shouldShowReadOffline()),
      onClick,
      text: t("global-bar.device-app", `${deviceCheck()} App`, {
        device: deviceCheck()
      })
    },
    other
  );

  return MenuItem(args);
};

export const brandSpecificApps = () => {
  const state = store.getState();
  const user = currentUser();
  const appMenuElements = brandSelectors.getBrandValue(
    state,
    "AppMenu.elements"
  );

  return appMenuElements.map(element => {
    const onClick = event => {
      event.preventDefault();
      if (element.lti && user.contextTokens) {
        ltiLaunch(user, element.lti.type, element.newTab, false, {
          label: element.label
        });
      } else if (element.sso) {
        navigateTo(
          event,
          element.url,
          `header/${element.key || element.label}`,
          event,
          element.newTab
        );
      } else {
        const target = element.newTab ? "_blank" : false;
        navigateSafelyTo(
          event,
          element.url,
          `header/${element.key || element.label}`,
          event,
          target
        );
      }
    };
    return MenuItem({
      text: element.label,
      onClick,
      condition: element.enabled,
      icon: element.customIcon ? "custom" : "default",
      iconLetter: element.iconLetter,
      customIcon: element.customIcon
    });
  });
};
